import { useStyles, Button } from '@archipro-design/aria';
import { PushPinFill, PushPinLine } from '@archipro-design/icons';
import * as S from './DetailPageButtons.style';
import { IslandTooltip } from './IslandTooltip';

interface PinToDirectoryButtonProps {
    isPinned: boolean;
    /** Pinning is only available while the project is in the directory. */
    isInDirectory: boolean;
    onToggle: (pinned: boolean) => void;
}

export const PinToDirectoryButton = ({
    isPinned,
    isInDirectory,
    onToggle,
}: PinToDirectoryButtonProps) => {
    const { css } = useStyles();

    // Profile-only projects never show as pinned: leaving the directory
    // auto-unpins on the server, so any lingering pinned flag is stale.
    const showAsPinned = isPinned && isInDirectory;

    const tooltipLabel = !isInDirectory
        ? 'Add to directory to enable pinning'
        : showAsPinned
          ? 'Unpin from top of directory'
          : 'Pin to top of directory';

    return (
        <IslandTooltip label={tooltipLabel}>
            <Button
                aria-label={tooltipLabel}
                className={css(S.ActionButton)}
                onClick={() => onToggle(!showAsPinned)}
                disabled={!isInDirectory}
                // A disabled button swallows pointer events, and with them the
                // hover that raises the label — which is exactly the state that
                // most needs explaining. The wrapper keeps the hover.
                styles={!isInDirectory ? { pointerEvents: 'none' } : undefined}
                icon={showAsPinned ? <PushPinFill /> : <PushPinLine />}
                size={20}
                color="white"
                variables={{
                    iconPadding: 0,
                    // 13px at the 1920 design frame. In rem, not a raw px string,
                    // so it scales with the vw-anchored root like the numeric
                    // tokens do; it holds the circle at its original size now
                    // that the icon inside it is smaller.
                    buttonPadding: '0.8125rem',
                    buttonRadius: `50%`,
                }}
            />
        </IslandTooltip>
    );
};
